import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";
import Button from "@mui/material/Button";
import { Truck } from "../../../types";
import { deleteEntry, deleteTruck } from "../../../utils/api";

interface DeleteConfirmModalProps {
  open: boolean;
  onClose: () => void;
  truck: Truck | Truck[];
  onDelete?: (truck: Truck) => void;
  groupedTrucks?: Record<string, Truck[]>;
  context?: "main-table" | "details-modal";
}

export function DeleteConfirmModal({
  open,
  onClose,
  truck,
  onDelete,
  groupedTrucks,
  context = "details-modal",
}: DeleteConfirmModalProps): React.JSX.Element {
  const truckToUse = Array.isArray(truck) ? truck[0] : truck;

  const getDeleteMessage = () => {
    if (context === "main-table") {
      return `Are you sure you want to delete the truck "${truckToUse.registration}"? This will permanently remove all data for this vehicle.`;
    } else {
      // In details modal, check how many entries exist for this truck registration
      const entriesForRegistration =
        groupedTrucks?.[truckToUse.registration] || [];
      const entryCount = entriesForRegistration.length;

      if (entryCount === 1) {
        return `Are you sure you want to delete the truck "${truckToUse.registration}"? This will permanently remove all data for this vehicle.`;
      } else {
        return `Are you sure you want to delete this specific entry for truck "${truckToUse.registration}"? This will only remove this particular entry, not the entire truck.`;
      }
    }
  };

  const handleConfirmDelete = async () => {
    try {
      if (context === "main-table") {
        // In main table, always delete the entire truck
        await deleteTruck(truckToUse.id);
      } else {
        // In details modal, check how many entries exist for this truck registration
        const entriesForRegistration =
          groupedTrucks?.[truckToUse.registration] || [];
        const entryCount = entriesForRegistration.length;

        if (entryCount === 1) {
          // If there's only one entry, delete the whole truck
          await deleteTruck(truckToUse.id);
        } else {
          // If there are multiple entries, delete just this specific entry
          await deleteEntry(truckToUse.id);
        }
      }

      if (onDelete) {
        onDelete(truckToUse);
      }

      onClose();
    } catch (error) {
      console.error("Error deleting entry:", error);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontFamily: "SigmaSans" }}>Confirm Delete</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {getDeleteMessage()}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleConfirmDelete} variant="contained" color="error">
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
}
